import { useRef, useEffect, FC, ReactNode, useState } from 'react';
import classNames from 'classnames';
import { useForm } from 'react-final-form';
import { useAppSelector } from 'helpers/hooks';
import { RadioField } from 'components/Forms/RadioField';
import { MultiSelectField } from 'components/Forms/MultiSelectField';
import { SwitchToggleField } from 'components/Forms/SwitchToggleField';
import { getRecipientsData } from 'selectors/recipients';
import { useFieldValue } from 'hooks/useFormHelpers';
import { MISSING_RECIPIENT_NOTE } from 'constants/recipients';
import { CustomOption } from './CustomOption';
import { DragNDropRecExpanded } from 'components/DragNDrop/DragNDropRecExpanded';
import { useFieldRestrictions } from 'hooks/useFieldRestrictions';
import { IPage } from 'store/pages/types';
import { ConfirmationModal } from 'components/Modal/confirmationModal';
import { AUTOPILOT_CLIENT_ID } from 'views/Pages/constants';

type Props = {
  isPreviewVisible: boolean;
  editPageSelected?: IPage;
  isPageDuplicated?: boolean;
  renderRowExtra?: (recipientId: string) => ReactNode;
  afterRecipients?: ReactNode;
};

export const RecipientCardsForm: FC<Props> = ({
  isPreviewVisible,
  editPageSelected,
  isPageDuplicated,
  renderRowExtra,
  afterRecipients,
}) => {
  const { change } = useForm();
  const recipients = useAppSelector(getRecipientsData);
  const fieldRestrictions = useFieldRestrictions(!!editPageSelected);
  const [isSearchableModalOpen, setIsSearchableModalOpen] = useState(false);
  const recurringEnabled = useFieldValue('recurring_enabled');

  const recipientsSelected = useFieldValue('recipients_selected');
  const recipientDetails = useFieldValue('recipient_details');
  const impactScoreVisibility = useFieldValue('impact_score_visibility');
  const processingRedirect = useFieldValue('processing_redirect');
  const recipientOrderBy = useFieldValue('recipient_order_by');
  const isImpactScoreMode = recipientOrderBy !== 'custom';
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const sortByImpactScore = (recipientsList: any[]) => {
    return [...recipientsList].sort((a, b) => {
      const recipientA = recipients.find(r => r.recipient_id === a.value);
      const recipientB = recipients.find(r => r.recipient_id === b.value);

      const scoreA = parseFloat(recipientA?.impact_score || '0');
      const scoreB = parseFloat(recipientB?.impact_score || '0');

      return scoreB - scoreA;
    });
  };

  const handleRecipientsChange = (newRecipients: any[]) => {
    if (isImpactScoreMode && newRecipients?.length > 0) {
      change('recipients_selected', sortByImpactScore(newRecipients));
    } else {
      change('recipients_selected', newRecipients);
    }
  };

  useEffect(() => {
    if (isImpactScoreMode && recipientsSelected?.length > 0 && recipients?.length > 0) {
      const sortedRecipients = sortByImpactScore(recipientsSelected);

      const hasOrderChanged = sortedRecipients.some(
        (rec, idx) => rec.value !== recipientsSelected[idx]?.value
      );

      if (hasOrderChanged) {
        change('recipients_selected', sortedRecipients);
      }
    }
  }, [recipientOrderBy, recipients, recipientsSelected]);

  const handlePublicSearchableChange = (value: boolean) => {
    if (value) {
      setIsSearchableModalOpen(true);
    }
  };

  const setRecipientDetails = (newRows: any) => {
    change('recipients_selected', newRows);
    if (isImpactScoreMode) {
      change('recipient_order_by', 'custom');
    }
  };

  const deleteRow = (recipientID: string) => {
    const updatedRecipients = recipientsSelected.filter(row => row.value !== recipientID);
    const updatedRecipientDetails = { ...recipientDetails };
    delete updatedRecipientDetails[recipientID];
    change('recipient_details', updatedRecipientDetails);
    change('recipients_selected', updatedRecipients);
  };

  const pageTypeViewOptions = [
    { value: 'LIST', label: 'List' },
    { value: 'COMPACT', label: 'Compact' },
  ];

  const recipientOptions =
    recipients
      ?.filter(rec =>
        processingRedirect === 'DE_OATH'
          ? rec.de_status === 'ACTIVE'
          : rec.stripe_status === 'ACTIVE'
      )
      ?.map(rec => ({
        value: rec.recipient_id,
        label: rec.recipient_name || '',
      })) || [];

  const paymentProcessorOptions = [
    { value: 'DE_OATH', label: 'Democracy Engine' },
    { value: 'STRIPE', label: 'Stripe' },
  ];

  const recipientArrangementOptions = [
    { value: 'custom', label: 'Manually' },
    { value: '', label: 'Oath Impact Score' },
  ];

  return (
    <div id="recipient-cards-form" className="form-group">
      <ConfirmationModal
        isOpen={isSearchableModalOpen}
        handleSave={() => {
          setIsSearchableModalOpen(false);
          change('is_public_searchable', true);
        }}
        handleCancel={() => {
          setIsSearchableModalOpen(false);
          change('is_public_searchable', false);
        }}
        warningMessage="Are you sure you want this page to appear as an Issue for all Oath donors in their profile?"
      />
      <span className="oath-eyebrow inkBlue">Recipient Cards</span>
      <div className="row">
        <div
          className={classNames('padding0', {
            'col-md-4': !isPreviewVisible,
            'col-md-6': isPreviewVisible,
          })}
        >
          <RadioField name="page_type" label="Page Type View:*" options={pageTypeViewOptions} />
        </div>
        {!isPreviewVisible && <div className="col-md-2 padding0" />}
        <div className="col-md-6 padding0">
          <div className="margin0 form-group">
            <SwitchToggleField name="impact_score_visibility" label="Impact Score Visibility" />
          </div>
          <div className="margin0 form-group">
            <SwitchToggleField name="show_more_visibility" label="'Show More' Visibility" />
          </div>
        </div>
      </div>
      <div className="row">
        {fieldRestrictions.canSee('processing_redirect') && (
          <div
            className={classNames('padding0', {
              'col-md-4': !isPreviewVisible,
              'col-md-6': isPreviewVisible,
            })}
          >
            <RadioField
              name="processing_redirect"
              label="Search Recipients by:*"
              options={paymentProcessorOptions}
              disabled={fieldRestrictions.isDisabled('processing_redirect')}
            />
          </div>
        )}
        {fieldRestrictions.canSee('is_public_searchable') && (
          <>
            {!isPreviewVisible && <div className="col-md-2 padding0" />}
            <div className="col-md-6 padding0">
              <div className="margin0 form-group">
                <SwitchToggleField
                  name="is_public_searchable"
                  label="Public Searchable"
                  disabled={fieldRestrictions.isDisabled('is_public_searchable')}
                  onChange={handlePublicSearchableChange}
                />
              </div>
            </div>
          </>
        )}
      </div>
      <div className="row">
        <div
          className={classNames({
            'col-md-6 padding-left0': !isPreviewVisible,
            'col-md-12 padding0': isPreviewVisible,
          })}
        >
          <MultiSelectField
            name="recipients_selected"
            label="Recipients"
            placeholder="Search"
            value={recipientsSelected}
            handleChange={handleRecipientsChange}
            options={recipientOptions}
            customOption={CustomOption}
            isDisabled={!!editPageSelected?.[0]?.recurring_enabled && !isPageDuplicated}
          />
        </div>
        <div
          className={classNames('padding0', {
            'col-md-6': !isPreviewVisible,
            'col-md-12': isPreviewVisible,
          })}
        >
          <div
            className={`recipient-label oath-utility ${classNames({ margin0: isPreviewVisible })}`}
          >
            {MISSING_RECIPIENT_NOTE}
          </div>
        </div>
      </div>
      {recipientsSelected?.length > 0 && (
        <>
          <div className="oath-utility gray">
            By default, recipients will appear in order of their Impact Scores. You can adjust
            recipients as you wish.
          </div>
          {recurringEnabled && (
            <div className="oath-utility errorRed">
              {editPageSelected?.[0]?.client_id !== AUTOPILOT_CLIENT_ID
                ? `Warning: Recipients cannot be added or removed because recurring donations are enabled for this page.`
                : `Warning: Do not add more than 5 recipients to this Oath on Autopilot page.`}
            </div>
          )}
          <DragNDropRecExpanded
            rows={recipientsSelected}
            setRows={setRecipientDetails}
            deleteRow={deleteRow}
            withDetails={false}
            impactScoreVisibility={impactScoreVisibility}
            disableDelete={!!editPageSelected?.[0]?.recurring_enabled && !isPageDuplicated}
            renderRowExtra={renderRowExtra}
            afterRows={afterRecipients}
          />
          <div
            className={classNames('padding0', {
              'col-md-4': !isPreviewVisible,
              'col-md-6': isPreviewVisible,
            })}
          >
            <RadioField
              name="recipient_order_by"
              label="Recipient Arrangement"
              options={recipientArrangementOptions}
            />
          </div>
        </>
      )}
      {!recipientsSelected?.length && afterRecipients}
    </div>
  );
};
